import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useUpcomingDeadlines } from '../hooks';
import { formatDate, formatDateTime } from '../../../config/appConfig';

export interface UpcomingDeadlinesProps {
  limit?: number;
  days?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const UpcomingDeadlines: React.FC<UpcomingDeadlinesProps> = ({ days: daysProp, embedded, settings, isEditing }) => {
  const [daysLocal, setDaysLocal] = useState(daysProp ?? 30);
  const days = daysProp ?? daysLocal;
  const { data, isLoading } = useUpcomingDeadlines(days);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Deadlines</h2>
        </div>
        <div className="h-[300px] flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading deadlines...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming Deadlines</h2>
          <select
            value={days}
            onChange={(e) => setDaysLocal(Number(e.target.value))}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value={7}>Next 7 days</option>
            <option value={14}>Next 14 days</option>
            <option value={30}>Next 30 days</option>
            <option value={60}>Next 60 days</option>
          </select>
        </div>
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No upcoming deadlines
        </div>
      </div>
    );
  }

  // Group by date
  const groupedByDate = data.reduce((acc, deadline) => {
    const date = deadline.dueDate.split('T')[0];
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(deadline);
    return acc;
  }, {} as Record<string, typeof data>);

  const sortedDates = Object.keys(groupedByDate).sort();

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'task':
        return '✓';
      case 'appointment':
        return '📅';
      case 'invoice':
        return '💰';
      default:
        return '•';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'task':
        return 'bg-blue-100 text-blue-700';
      case 'appointment':
        return 'bg-teal-100 text-teal-700';
      case 'invoice':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'text-red-600 font-semibold';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date();
  };

  const isToday = (dueDate: string) => {
    const today = new Date().toISOString().split('T')[0];
    return dueDate.split('T')[0] === today;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Upcoming Deadlines</h2>
        <select
          value={days}
          onChange={(e) => setDaysLocal(Number(e.target.value))}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        >
          <option value={7}>Next 7 days</option>
          <option value={14}>Next 14 days</option>
          <option value={30}>Next 30 days</option>
          <option value={60}>Next 60 days</option>
        </select>
      </div>

      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {sortedDates.map((date) => {
          const deadlines = groupedByDate[date];
          const overdue = isOverdue(date);
          const today = isToday(date);

          return (
            <div key={date} className="space-y-2">
              <div className={`text-sm font-semibold ${
                overdue ? 'text-red-600' : today ? 'text-blue-600' : 'text-gray-700'
              }`}>
                {overdue ? '⚠️ Overdue: ' : today ? '📅 Today: ' : ''}
                {formatDate(date)}
              </div>
              <div className="space-y-2 pl-4 border-l-2 border-gray-200">
                {deadlines.map((deadline) => (
                  <Link
                    key={deadline.id}
                    to={deadline.link}
                    className="block p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(deadline.type)}`}>
                            {getTypeIcon(deadline.type)} {deadline.type}
                          </span>
                          {deadline.priority && (
                            <span className={`text-xs ${getPriorityColor(deadline.priority)}`}>
                              {deadline.priority}
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {deadline.title}
                        </div>
                        {deadline.status && (
                          <div className="text-xs text-gray-500 mt-1">
                            Status: {deadline.status}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 whitespace-nowrap">
                        {formatDateTime(deadline.dueDate)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingDeadlines;
